//src/hooks/useSurveyRiskSuggestions.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveAndConvertSurveyRiskSuggestion,
  approveSurveyRiskSuggestion,
  convertSurveyRiskSuggestion,
  getSurveyRiskSuggestionDetail,
  getSurveyRiskSuggestionsSummary,
  listSurveyRiskSuggestionLogs,
  listSurveyRiskSuggestionsQueue,
  rejectSurveyRiskSuggestion,
  resetSurveyRiskSuggestion,
} from "@/services/surveyRiskSuggestions";
import type {
  SurveyRiskSuggestionApproveIn,
  SurveyRiskSuggestionConvertIn,
  SurveyRiskSuggestionRejectIn,
  SurveyRiskSuggestionResetIn,
} from "@/types/surveyRiskSuggestions";

const QUEUE_KEY = "survey_risk_suggestions_queue";

export function useSurveyRiskSuggestionsQueue(params: {
  campaign_id?: string;
  status?: string;
  cut_type?: string;
  risk_level_code?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: [QUEUE_KEY, params],
    queryFn: () => listSurveyRiskSuggestionsQueue(params),
    staleTime: 15_000,
  });
}

export function useSurveyRiskSuggestionsSummary(params: {
  campaign_id?: string;
  status?: string;
  cut_type?: string;
  risk_level_code?: string;
}) {
  return useQuery({
    queryKey: ["survey_risk_suggestions_summary", params],
    queryFn: () => getSurveyRiskSuggestionsSummary(params),
    staleTime: 15_000,
  });
}

export function useSurveyRiskSuggestionDetail(suggestionId?: string) {
  return useQuery({
    queryKey: ["survey_risk_suggestion_detail", suggestionId],
    queryFn: () => getSurveyRiskSuggestionDetail(suggestionId as string),
    enabled: !!suggestionId,
    staleTime: 15_000,
  });
}

export function useSurveyRiskSuggestionLogs(suggestionId?: string) {
  return useQuery({
    queryKey: ["survey_risk_suggestion_logs", suggestionId],
    queryFn: () => listSurveyRiskSuggestionLogs(suggestionId as string),
    enabled: !!suggestionId,
    staleTime: 5_000,
  });
}

function invalidateSuggestionQueries(qc: ReturnType<typeof useQueryClient>, suggestionId?: string) {
  qc.invalidateQueries({ queryKey: [QUEUE_KEY] });
  qc.invalidateQueries({ queryKey: ["survey_risk_suggestions_summary"] });
  qc.invalidateQueries({ queryKey: ["risk_inventories"] });
  qc.invalidateQueries({ queryKey: ["risk_inventory_detail"] });
  qc.invalidateQueries({ queryKey: ["risk_inventory_items"] });
  if (suggestionId) {
    qc.invalidateQueries({ queryKey: ["survey_risk_suggestion_detail", suggestionId] });
    qc.invalidateQueries({ queryKey: ["survey_risk_suggestion_logs", suggestionId] });
  }
}

export function useApproveSurveyRiskSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId, payload }: { suggestionId: string; payload: SurveyRiskSuggestionApproveIn }) =>
      approveSurveyRiskSuggestion(suggestionId, payload),
    onSuccess: (_data, variables) => invalidateSuggestionQueries(qc, variables.suggestionId),
  });
}

export function useRejectSurveyRiskSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId, payload }: { suggestionId: string; payload: SurveyRiskSuggestionRejectIn }) =>
      rejectSurveyRiskSuggestion(suggestionId, payload),
    onSuccess: (_data, variables) => invalidateSuggestionQueries(qc, variables.suggestionId),
  });
}

export function useResetSurveyRiskSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId, payload }: { suggestionId: string; payload?: SurveyRiskSuggestionResetIn }) =>
      resetSurveyRiskSuggestion(suggestionId, payload),
    onSuccess: (_data, variables) => invalidateSuggestionQueries(qc, variables.suggestionId),
  });
}

export function useConvertSurveyRiskSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId, payload }: { suggestionId: string; payload?: SurveyRiskSuggestionConvertIn }) =>
      convertSurveyRiskSuggestion(suggestionId, payload),
    onSuccess: (_data, variables) => invalidateSuggestionQueries(qc, variables.suggestionId),
  });
}

export function useApproveAndConvertSurveyRiskSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ suggestionId, payload }: { suggestionId: string; payload?: SurveyRiskSuggestionConvertIn }) =>
      approveAndConvertSurveyRiskSuggestion(suggestionId, payload),
    onSuccess: (_data, variables) => invalidateSuggestionQueries(qc, variables.suggestionId),
  });
}
